import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toIsoDate } from "../../utils/dateFormat";
import IsoDateInput from "../common/Isodateinput";
import { useDispatch } from "react-redux";
import { getMonthlyAllocationByYearAndMonth } from "../../store/slices/monthlyAllocationSlice";
import SearchableSelect from "../common/SearchableSelect";

const initialForm = {
  id: null,
  code: "",
  name: "",
  periodType: "M",
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  startDate: "",
  endDate: "",
  attendanceStartDate: "",
  attendanceEndDate: "",
  otStartDate: "",
  otEndDate: "",
  payDate: toIsoDate(new Date()),
};

const months = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const getCurrentYear = () => new Date().getFullYear();
const getCurrentMonth = () => new Date().getMonth() + 1;

// const getAvailableMonths = (year) => {
//   const currentYear = getCurrentYear();
//   const currentMonth = getCurrentMonth();

//   if (Number(year) > currentYear) {
//     return months;
//   }

//   if (Number(year) === currentYear) {
//     return months.filter((month) => month.value >= currentMonth);
//   }

//   return [];
// };

const getAvailableMonths = (year) => {
  return months; 
}


const REQUIRED_MONTHLY_ALLOCATION_TYPES = [
  { code: "C", label: "Contract" },
  { code: "E", label: "Daily Wages" },
]

const inputClass = `
  w-full h-14
  rounded-md
  border border-surface-200
  bg-white
  px-3.5
  text-[16px] text-surface-800
  outline-none
  transition-colors
  focus:border-primary-500
  focus:ring-1 focus:ring-primary-500
`;

const labelClass = `
  absolute
  left-3
  top-0
  -translate-y-1/2
  bg-white
  px-1.5
  text-[13px]
  text-surface-500
  pointer-events-none
  z-10
`;

const pad2 = (value) => String(value).padStart(2, "0")

const getMonthDateRange = (year, month) => {
  // month is 1-based
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);

  return {
    startDate: `${start.getFullYear()}-${pad2(start.getMonth() + 1)}-01`,
    endDate: `${end.getFullYear()}-${pad2(end.getMonth() + 1)}-${pad2(end.getDate())}`,
  };
};

const getPreviousMonthDateRange = (year, month) => {
  // month is 1-based
  const previousMonth = new Date(year, month - 2, 1);

  return getMonthDateRange(
    previousMonth.getFullYear(),
    previousMonth.getMonth() + 1
  );
};


const getMonthName = (month) => {
  return months.find((item) => item.value === Number(month))?.label || "";
}

const getDateDifferenceInDays = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

const rangesOverlap = (startA, endA, startB, endB) => {
  if (!startA || !endA || !startB || !endB) {
    return false;
  }

  return startA <= endB && endA >= startB;
}

const getExistingPeriodStart = (period) => toIsoDate(period.startDate);
const getExistingPeriodEnd = (period) => toIsoDate(period.endDate);

const generatePeriodCode = (periodType, startDate, endDate) => {
  if (!periodType || !startDate || !endDate) {
    return ""
  }

  const [startYear, startMonth, startDay] = startDate.split("-");
  const [_, endMonth, endDay] = endDate.split("-");

  const year = startYear.slice(-2);

  return `${year}${periodType}-${startMonth}${startDay}-${endMonth}${endDay}`
}

const generatePeriodName = (periodType, year, month, startDate, endDate) => {
  if (!year || !month) {
    return ""
  }

  const monthName = getMonthName(month);

  if (periodType === "M") {
    return `${year} ${monthName}`
  }

  if (periodType === "W" && startDate && endDate) {
    const startDay = startDate.split("-")[2];
    const endDay = endDate.split("-")[2];

    return `${year} ${monthName} ${startDay}-${endDay}`;
  }

  return `${year} ${monthName}`;
}

function Field({ label, error, children }) {
  return (
    <div className="relative">
      <label className={labelClass}>
        {label}
      </label>

      {children}

      {error && (
        <p className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function TextInput({ label, value, onChange, onBlur, type = "text", disabled = false, readOnly = false, min, max }) {
  return (
    <Field label={label}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        className={`${inputClass} ${
          disabled || readOnly ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
        }`}
        disabled={disabled}
        readOnly={readOnly}
        min={min}
        max={max}
      /> 
    </Field>
  );
}

function DateInput({ label, value, onChange, error = "", disabled = false }) {
  return (
    <Field label={label} error={error}>
      <IsoDateInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={error}
        className={`${inputClass} appearance-none ${
          error ? "border-red-400 focus:border-red-500 focus:ring-red-500" : ""
        } ${
          disabled ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
        }`}
      />
    </Field>
  );
}

function SelectInput({ label, value, onChange, children, disabled = false, }) {
  return (
    <Field label={label}>
      <select
        value={value}
        onChange={onChange}
        className={`${inputClass} appearance-auto cursor-pointer ${
          disabled ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
        }`}
        disabled={disabled}
      >
        {children}
      </select>
    </Field>
  );
}

export default function AddPayRollPeriodDialog({
  open,
  onClose,
  onSave,
  loading,
  existingPeriods = [],
  mode = 'add',
  period = null,
}) {
  const dispatch = useDispatch();

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [missingMonthlyAllocations, setMissingMonthlyAllocations] = useState([]);
  const [checkingMonthlyAllocation, setCheckingMonthlyAllocation] = useState(false);

  const isView = mode === "view";
  const isEdit = mode === "edit";

  const checkMonthlyAllocations = async (year, month) => {
    if (!year || !month || !REQUIRED_MONTHLY_ALLOCATION_TYPES.length) {
      setMissingMonthlyAllocations([]);
      return;
    }

    setCheckingMonthlyAllocation(true);

    try {
      const result = await dispatch(getMonthlyAllocationByYearAndMonth({
        year: Number(year),
        month: Number(month),
      })).unwrap();

      const allocations = Array.isArray(result) ? result : [];

      const activeCategoryCodes = new Set(
        allocations.filter(
          (allocation) => String(allocation.HMA_IS_ACTIVE).toLowerCase() === "true" || String(allocation.HMA_IS_ACTIVE) === "1"
        )
        .map((allocation) => String(allocation.HMA_CATEGORY_CODE).toUpperCase())
      );

      const missing = REQUIRED_MONTHLY_ALLOCATION_TYPES.filter(
        (type) => !activeCategoryCodes.has(type.code.toUpperCase())
      );

      setMissingMonthlyAllocations(missing);
    } catch (error) {
      console.error("Failed to check monthly allocations: ", error);
      setMissingMonthlyAllocations([]);
    } finally {
      setCheckingMonthlyAllocation(false);
    }
  }

  useEffect(() => {
    if (!open) {
      return;
    }

    setErrors({});
    setMissingMonthlyAllocations([]);

    if (!period || mode === "add") {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;

      const { startDate: payrollStartDate, endDate: payrollEndDate, } = getMonthDateRange(year, month);
      // const {startDate: previousStartDate, endDate: previousEndDate, } = getPreviousMonthDateRange(year, month);

      setForm({
        ...initialForm,
        year,
        month,
        startDate: payrollStartDate,
        endDate: payrollEndDate,
        attendanceStartDate: payrollStartDate,
        attendanceEndDate: payrollEndDate,
        otStartDate: payrollStartDate,
        otEndDate: payrollEndDate,
        payDate: toIsoDate(today),
        name: generatePeriodName("M", year, month, payrollStartDate, payrollEndDate),
        code: generatePeriodCode("M", payrollStartDate, payrollEndDate),
      });

      checkMonthlyAllocations(year, month);

      return;
    }

    setForm({
      id: period.id,

      code: period.code ?? "",
      name: generatePeriodName(
        period.frequency === "Weekly" ? "W" : "M",
        period.year ?? new Date().getFullYear(),
        period.month ?? new Date().getMonth() + 1,
        toIsoDate(period.startDate) ?? "",
        toIsoDate(period.endDate) ?? ""
      ),

      periodType: period.frequency === "Weekly" ? "W" : "M",

      year: period.year ?? new Date().getFullYear(),
      month: period.month ?? new Date().getMonth() + 1,

      startDate: toIsoDate(period.startDate) ?? "",
      endDate: toIsoDate(period.endDate) ?? "",

      attendanceStartDate: toIsoDate(period.attendanceStartDate) ?? "",
      attendanceEndDate: toIsoDate(period.attendanceEndDate) ?? "",

      otStartDate: toIsoDate(period.otStartDate) ?? "",
      otEndDate: toIsoDate(period.otEndDate) ?? "",

      payDate: toIsoDate(period.payDate) ?? "",
    });
  }, [open, mode, period]);

  const handleChange = (field) => (valueOrEvent) => {
    const value = valueOrEvent?.target !== undefined
      ? valueOrEvent.target.value
      : valueOrEvent;

    setForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      // Payroll start changes -> automatically update attendance start + OT start
      // if (field === "startDate") {
      //   next.attendanceStartDate = value;
      //   next.otStartDate = value;
      // }

      // Payroll end changes -> automatically update attendance end + OT end
      // if (field === "endDate") {
      //   next.attendanceEndDate = value;
      //   next.otEndDate = value;
      // }

      // Year/month/periodType changed -> automatically generate payroll name
      if (field === "periodType" || field === "year" || field === "month") {
        const periodType = field === "periodType" ? value : prev.periodType;
        const year = field === "year" ? Number(value) : prev.year;
        const month = field === "month" ? Number(value) : prev.month;

        next.name = generatePeriodName(periodType, year, month, next.startDate, next.endDate);
      }

      // Start / End changed -> Weekly name depends on the actual dates
      if (field === "startDate" || field === "endDate") {
        next.name = generatePeriodName(next.periodType, next.year, next.month, next.startDate, next.endDate)
      }

      // When changing the year/month for a NEW payroll period, automatically calculate
      // previous month's payroll dates. Existing ones retain their manual dates.
      if (mode === "add" && (field === "year" || field === "month")) {
        const year = field === "year" ? Number(value) : Number(prev.year);
        const month = field === "month" ? Number(value) : Number(prev.month);

        // Selected month actual date range
        const { startDate: payrollStartDate, endDate: payrollEndDate } = getMonthDateRange(year, month);

        // Previous month actual date range 
        // const { startDate: previousStartDate, endDate: previousEndDate } = getPreviousMonthDateRange(year, month);

        next.startDate = payrollStartDate;
        next.endDate = payrollEndDate;

        next.attendanceStartDate = payrollStartDate;
        next.attendanceEndDate = payrollEndDate;

        next.otStartDate = payrollStartDate;
        next.otEndDate = payrollEndDate;

        next.name = generatePeriodName(next.periodType, year, month, payrollStartDate, payrollEndDate);
        next.code = generatePeriodCode(next.periodType, payrollStartDate, payrollEndDate);

        checkMonthlyAllocations(year, month);
      }

      // Regenerate name when period information changes
      if (
        field === "periodType" ||
        field === "year" ||
        field === "month" ||
        field === "startDate" ||
        field === "endDate"
      ) {
        next.name = generatePeriodName(
          next.periodType,
          Number(next.year),
          Number(next.month),
          next.startDate,
          next.endDate
        );
      }

      if (
        field === "periodType" ||
        field === "startDate" ||
        field === "endDate" ||
        field === "year" ||
        field === "month"
      ) {
        next.code = generatePeriodCode(
          next.periodType,
          next.startDate,
          next.endDate
        );
      }

      return next;
    })

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
      general: undefined,
    }))
  };

  const handleSave = () => {
    if (isView) return;

    const nextErrors = {};

    // --- Required dates ---
    if (!form.startDate) {
      nextErrors.startDate = "Payroll start date is required.";
    }

    if (!form.endDate) {
      nextErrors.endDate = "Payroll end date is required.";
    }

    if (Number(form.year) < getCurrentYear()) {
      nextErrors.general = "Payroll year cannot be before the current year.";
    }

    // --- Date range validation ---
    if (form.startDate && form.endDate) {
      const days = getDateDifferenceInDays(form.startDate, form.endDate);

      if (days < 0) {
        nextErrors.endDate = "Payroll end date cannot be before the start date.";
      }

      // weekly periods must be exactly 7 days
      if (form.periodType === "W" && days !== 6) {
        nextErrors.endDate = "A weekly payroll period must contain exactly 7 days.";
      }
    }

    // --- Overlap validation ---
    // Monthly can overlap weekly.
    // Weekly cannot overlap weekly.
    // Monthly cannot overlap monthly.
    if (
      form.startDate &&
      form.endDate &&
      !nextErrors.startDate &&
      !nextErrors.endDate
    ) {
      const overlappingPeriod = existingPeriods.find((existingPeriod) => {
        // Ignore the record currently being edited
        if (
          mode === "edit" &&
          period?.id != null &&
          existingPeriod.id === period.id
        ) {
          return false;
        }

        const existingFrequency = existingPeriod.frequency === "Weekly" || existingPeriod.periodType === "W" ? "W" : "M";

        // Different frequencies are allowed to overlap
        if (existingFrequency !== form.periodType) {
          return false;
        }

        const existingStart = toIsoDate(getExistingPeriodStart(existingPeriod));
        const existingEnd = toIsoDate(getExistingPeriodEnd(existingPeriod));

        return rangesOverlap(
          form.startDate,
          form.endDate,
          existingStart,
          existingEnd
        )
      })

      if (overlappingPeriod) {
        const existingStart = getExistingPeriodStart(overlappingPeriod);
        const existingEnd = getExistingPeriodEnd(overlappingPeriod);

        nextErrors.general = `${form.periodType === "W" ? "Weekly" : "Monthly"} payroll period overlaps with "${overlappingPeriod.name} (${existingStart} to ${existingEnd})"`;
      }
    }

    setErrors(nextErrors);

    // Don't save if validation failed
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave({
      ...form,
      status: "A",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className="!px-6 !pt-5 !pb-3">
        <span className="text-[21px] font-medium text-surface-900">
          {mode === "view" ? "View Payroll Period" : mode === "edit" ? "Edit Payroll Period" : "New Payroll Period"}
        </span>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5">
        {errors.general && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errors.general}
          </div>
        )}
        
        {missingMonthlyAllocations.length > 0 && (
          <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            <div className="font-medium">
              Monthly allocation is missing for the selected period.
            </div>

            <div className="mt-1">
              Missing employee type
              {missingMonthlyAllocations.length > 1 ? "s" : ""}:{" "}
              <span className="font-semibold">
                {missingMonthlyAllocations.map((type) => type.label).join(", ")}
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

          {/* Period Code */}
          <Field label="Period Code (Generated)">
            <input 
              type="text"
              placeholder="XXX-XXXX-XXXX"
              value={form.code}
              readOnly
              className={`${inputClass} bg-gray-50 text-gray-500 cursor-not-allowed`}
            />
          </Field>

          <Field label="Period Name (generated)">
            <input 
              type="text"
              placeholder={new Date().toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
              value={form.name}
              readOnly
              className={`${inputClass} bg-gray-50 text-gray-500 cursor-not-allowed`}
            />
          </Field>

          {/* Payment Date */}
          <DateInput
            label="Payment Date"
            value={form.payDate}
            onChange={handleChange("payDate")}
            disabled={isView}
          />

          {/* Period Type */}
          <SelectInput
            label="Period Type"
            value={form.periodType}
            onChange={handleChange("periodType")}
            disabled={isView}
          >
            <option value="M">Monthly</option>
            <option value="W">Weekly</option>
          </SelectInput>

          {/* Year */}
          <TextInput
            label="Year"
            type="number"
            min={getCurrentYear()}
            value={form.year}
            onChange={handleChange("year")}
            onBlur={(e) => {
              if (Number(e.target.value) < getCurrentYear()) {
                handleChange("year")(getCurrentYear());
              }
            }}
            disabled={isView}
          />

          {/* Month */}
          {/* {form.periodType === "M" && ( */}
            {/* <SelectInput
              label="Month"
              value={form.month}
              onChange={handleChange("month")}
              disabled={isView}
            >
              {getAvailableMonths(form.year).map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </SelectInput> */}

            <Field label="Month">
              <SearchableSelect
                value={form.month}
                onChange={handleChange("month")}
                options={getAvailableMonths(form.year)}
                disabled={isView}
                searchPlaceholder="Search month..."
                className={`${inputClass} ${
                  isView ? "bg-gray-50 text-gray-500 cursor-not-allowed" : "cursor-pointer"
                }`}
              />
            </Field>
          {/* )} */}

          {/* Payroll */}
          <DateInput
            label="Payroll Start"
            value={form.startDate}
            onChange={handleChange("startDate")}
            error={errors.startDate}
            disabled={isView}
          />

          <DateInput
            label="Payroll End"
            value={form.endDate}
            onChange={handleChange("endDate")}
            error={errors.endDate}
            disabled={isView}
          />

          {/* Attendance */}
          <DateInput
            label="Attendance Start"
            value={form.attendanceStartDate}
            onChange={handleChange("attendanceStartDate")}
            disabled={isView}
          />

          <DateInput
            label="Attendance End"
            value={form.attendanceEndDate}
            onChange={handleChange("attendanceEndDate")}
            disabled={isView}
          />

          {/* OT */}
          <DateInput
            label="OT Start"
            value={form.otStartDate}
            onChange={handleChange("otStartDate")}
            disabled={isView}
          />

          <DateInput
            label="OT End"
            value={form.otEndDate}
            onChange={handleChange("otEndDate")}
            disabled={isView}
          />

        </div>
      </DialogContent>

      <DialogActions className="!px-6 !py-3">
        <Button
          onClick={onClose}
          className="!text-primary-600"
        >
          {isView ? "CLOSE" : "CANCEL"}
        </Button>

        {!isView && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            className="!bg-primary-600 hover:!bg-primary-700"
          >
            {loading ? "SAVING..." : isEdit ? "UPDATE" : "SAVE"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}